
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";

export const useUploadState = () => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [stateName, setStateName] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedLeague, setSelectedLeague] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [newCountry, setNewCountry] = useState('');
  const [newLeague, setNewLeague] = useState('');
  const [newYear, setNewYear] = useState('');
  const { toast } = useToast();

  // Add effect to update state name when selections change
  useEffect(() => {
    if (selectedCountry && selectedLeague && selectedYear) {
      let yearDisplay = selectedYear;
      if (!yearDisplay.includes('/')) {
        yearDisplay = `${yearDisplay}/${(parseInt(yearDisplay) + 1).toString()}`;
      }
      
      // Remove numeric prefix from league name for display
      const displayLeagueName = selectedLeague.replace(/^\d{2}\s+/, '');
      setStateName(`${selectedCountry} ${displayLeagueName} ${yearDisplay}`);
    } else {
      setStateName('');
    }
  }, [selectedCountry, selectedLeague, selectedYear]);

  const { data: countries = [], isError: isCountriesError } = useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('countries')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    staleTime: 30000,
    retry: 2
  });

  const { data: leagues = [], refetch: refetchLeagues } = useQuery({
    queryKey: ['leagues', selectedCountry],
    queryFn: async () => {
      if (!selectedCountry) return [];
      
      const { data, error } = await supabase
        .from('leagues')
        .select('*')
        .eq('country', selectedCountry)
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedCountry,
    staleTime: 30000,
    retry: 2
  });

  const { data: years = [], refetch: refetchYears } = useQuery({
    queryKey: ['years'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('years')
        .select('*')
        .order('year', { ascending: false });

      if (error) throw error;
      return data.map(yearData => ({
        id: yearData.year,
        year: yearData.year.includes('/') ? yearData.year : `${yearData.year}/${(parseInt(yearData.year) + 1).toString()}`
      }));
    },
    staleTime: 30000,
    retry: 2
  });

  const resetForm = () => {
    setTableData([]);
    setStateName('');
    setSelectedCountry('');
    setSelectedLeague('');
    setSelectedYear('');
    setSortColumn(null);
    setSortDirection('asc');
  };

  const isError = isCountriesError;

  return {
    tableData,
    setTableData,
    sortColumn,
    setSortColumn,
    sortDirection,
    setSortDirection,
    stateName,
    setStateName,
    selectedCountry,
    setSelectedCountry,
    selectedLeague,
    setSelectedLeague,
    selectedYear,
    setSelectedYear,
    newCountry,
    setNewCountry,
    newLeague,
    setNewLeague,
    newYear,
    setNewYear,
    countries,
    leagues,
    years,
    refetchLeagues,
    refetchYears,
    resetForm,
    toast,
    isError
  };
};
